import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const MOBILE_DRAWER = '(max-width: 989px)';
const HEADER_BORDER_OVERLAP = 1;
const SUBMENU = 'details[data-drawer-submenu]';
const PRODUCT = 'details[data-drawer-product]';

/** All drawer transitions have one owner. Async completions check an operation
 * token before changing state; closing/reopening never requires an observer to
 * force a details element back open. Native accordion behavior stays separate. */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  static active = null;
  #controller = null;
  #frames = new Set();
  #generation = 0;
  #operations = new WeakMap();
  #submenu = null;
  #product = null;
  #state = 'closed';
  #mobile = matchMedia(MOBILE_DRAWER);
  #header = null;
  #headerShell = null;
  #headerSnapshot = null;
  #resizeObserver = null;
  #positionFrame = null;

  get isOpen() { return this.#state === 'open' || this.#state === 'opening'; }

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#header = this.closest('header-component, #header-component');
    this.#headerShell = this.#header?.closest('.header-section, .shopify-section') ?? this.#header?.parentElement;
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    this.addEventListener('animationend', this.#onAnimationEnd, { signal });
    window.addEventListener('resize', this.#schedulePosition, { passive: true, signal });
    this.#mobile.addEventListener('change', this.#onBreakpoint, { signal });
    if (this.#header) {
      this.#resizeObserver = new ResizeObserver(this.#schedulePosition);
      this.#resizeObserver.observe(this.#header);
    }
    // A reconnected/section-rendered node starts from a consistent closed state.
    this.#reset();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    for (const id of this.#frames) cancelAnimationFrame(id);
    this.#frames.clear();
    this.#positionFrame = null;
    this.#reset();
    super.disconnectedCallback();
  }

  /** Called by Component's existing on:click bridge; never double-bind it. */
  toggle(event) {
    event?.preventDefault();
    if (this.isOpen) this.close();
    else this.open();
  }

  open(target, event) {
    if (target instanceof Event) { event = target; target = undefined; }
    event?.preventDefault();
    if (target === 'product') return this.#openProduct(this.#detailsFrom(event, PRODUCT));
    if (target === 'submenu') return this.#openSubmenu(this.#detailsFrom(event, SUBMENU));
    if (this.isOpen) return;
    if (this.#state === 'closing') this.#reset();
    if (HeaderDrawer.active && HeaderDrawer.active !== this) HeaderDrawer.active.close(undefined, true);
    HeaderDrawer.active = this;
    const generation = ++this.#generation;
    this.#state = 'opening';
    const details = this.refs.details;
    this.#setExpanded(details, true);
    this.refs.menuDrawer.inert = false;
    this.#pinHeader();
    this.#suppressInitialAccordionAnimation();
    // Establish the translated starting style before starting the transition.
    void this.refs.menuDrawer.offsetWidth;
    this.#frame(() => {
      if (generation !== this.#generation || !this.isOpen) return;
      details.classList.add('menu-open');
      this.#afterMotion(this.refs.menuDrawer, () => {
        if (generation !== this.#generation || !this.isOpen) return;
        this.#state = 'open';
        this.#focusCurrent();
      });
    });
  }

  close(event, immediate = false) {
    event?.preventDefault?.();
    if (this.#state === 'closed') return;
    if (immediate) { this.#reset(); return; }
    if (this.#state === 'closing') return;
    const generation = ++this.#generation;
    this.#state = 'closing';
    this.#operations = new WeakMap();
    this.refs.menuDrawer.inert = true;
    this.refs.details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    this.refs.details.classList.remove('menu-open');
    this.#releaseFocus();
    this.refs.details.querySelector(':scope > summary')?.focus({ preventScroll: true });
    this.#afterMotion(this.refs.menuDrawer, () => {
      if (generation === this.#generation && this.#state === 'closing') this.#reset();
    });
  }

  back(event) {
    event?.preventDefault();
    const target = event?.target;
    if (target instanceof Element && target.closest(PRODUCT)) {
      this.#closeProduct(target.closest(PRODUCT));
    } else if (this.#submenu) {
      this.#closeSubmenu(this.#submenu);
    } else {
      this.close();
    }
  }

  #detailsFrom(event, selector) {
    const details = event?.target instanceof Element ? event.target.closest(selector) : null;
    return details instanceof HTMLDetailsElement && this.contains(details) ? details : null;
  }

  #setExpanded(details, expanded) {
    details.open = expanded;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(expanded));
  }

  #openSubmenu(details) {
    if (!details || !this.isOpen) return;
    if (this.#submenu === details && details.classList.contains('menu-open')) return;
    if (this.#submenu && this.#submenu !== details) this.#finishSubmenu(this.#submenu, false);
    this.#submenu = details;
    this.#operations.set(details, Symbol());
    this.#setExpanded(details, true);
    const panel = details.querySelector(':scope > .menu-drawer__submenu');
    if (panel) panel.inert = false;
    details.classList.add('menu-open');
    this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    const generation = this.#generation;
    this.#frame(() => {
      if (generation !== this.#generation || this.#submenu !== details || !this.isOpen) return;
      this.#openAutomaticProduct(details);
      this.#focusCurrent();
    });
  }

  #closeSubmenu(details) {
    if (!details || !this.isOpen) return;
    const operation = Symbol();
    this.#operations.set(details, operation);
    details.classList.remove('menu-open');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    const panel = details.querySelector(':scope > .menu-drawer__submenu');
    if (panel) panel.inert = true;
    this.#afterMotion(panel ?? details, () => {
      if (this.#operations.get(details) !== operation || !this.isOpen) return;
      this.#finishSubmenu(details, true);
    });
  }

  #finishSubmenu(details, focus) {
    this.#operations.delete(details);
    for (const product of details.querySelectorAll(PRODUCT)) this.#resetProduct(product);
    details.classList.remove('menu-open');
    this.#setExpanded(details, false);
    const panel = details.querySelector(':scope > .menu-drawer__submenu');
    if (panel) panel.inert = false;
    if (this.#submenu === details) this.#submenu = null;
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    if (focus && this.isOpen) {
      this.#focusCurrent();
      details.querySelector(':scope > summary')?.focus({ preventScroll: true });
    }
  }

  #openAutomaticProduct(details) {
    for (const product of details.querySelectorAll(`${PRODUCT}.menu-drawer__product-accordion--auto-open`)) {
      const accordion = product.closest('accordion-custom');
      if (accordion && !accordion.querySelector(':scope > details')?.open) continue;
      this.#openProduct(product);
      break;
    }
  }

  #openProduct(details) {
    if (!details || !this.isOpen) return;
    if (this.#product && this.#product !== details) this.#resetProduct(this.#product);
    this.#operations.set(details, Symbol());
    this.#product = details;
    this.#setExpanded(details, true);
    const panel = details.querySelector(':scope > .menu-drawer__product-panel');
    panel?.classList.remove('is-closing');
    if (panel) { panel.inert = false; panel.scrollTo({ top: 0, behavior: 'auto' }); }
    const generation = this.#generation;
    this.#frame(() => {
      if (generation !== this.#generation || this.#product !== details || !this.isOpen) return;
      this.#focusCurrent();
    });
  }

  #closeProduct(details) {
    if (!details?.open || !this.isOpen) return;
    if (details.classList.contains('menu-drawer__product-accordion--auto-open')) {
      this.#resetProduct(details);
      if (this.#submenu) this.#closeSubmenu(this.#submenu);
      return;
    }
    const operation = Symbol();
    this.#operations.set(details, operation);
    const panel = details.querySelector(':scope > .menu-drawer__product-panel');
    if (panel) { panel.inert = true; panel.classList.add('is-closing'); }
    this.#afterMotion(panel ?? details, () => {
      if (this.#operations.get(details) !== operation || !this.isOpen) return;
      this.#resetProduct(details);
      this.#focusCurrent();
      details.querySelector(':scope > summary')?.focus({ preventScroll: true });
    });
  }

  #resetProduct(details) {
    this.#operations.delete(details);
    this.#setExpanded(details, false);
    const panel = details.querySelector(':scope > .menu-drawer__product-panel');
    if (panel) { panel.inert = false; panel.classList.remove('is-closing'); }
    if (this.#product === details) this.#product = null;
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#product?.open) return this.#closeProduct(this.#product);
    const accordion = event.target instanceof Element ? event.target.closest('details[data-drawer-accordion][open]') : null;
    if (accordion) {
      accordion.open = false;
      accordion.querySelector(':scope > summary')?.focus({ preventScroll: true });
    } else if (this.#submenu) this.#closeSubmenu(this.#submenu);
    else this.close();
  };

  #onToggle = (event) => {
    const details = event.target;
    if (details === this.refs.details && !details.open && this.#state !== 'closed') this.#reset();
    // AccordionCustom may open a group through its media-query/default behavior.
    // Only react to a confirmed open group; never force its state with observers.
    if (details instanceof HTMLDetailsElement && details.matches('[data-drawer-accordion]') && details.open && this.#submenu?.contains(details) && this.isOpen) {
      this.#openAutomaticProduct(details);
    }
  };

  #onAnimationEnd = (event) => {
    if (event.target instanceof Element && event.target.matches('.menu-drawer__animated-element')) removeWillChangeOnAnimationEnd(event);
  };

  #focusCurrent() {
    if (!this.isOpen || HeaderDrawer.active !== this) return;
    let panel = this.refs.menuDrawer;
    if (this.#mobile.matches && this.#product?.open) panel = this.#product.querySelector(':scope > .menu-drawer__product-panel') ?? panel;
    else if (this.#submenu?.open) panel = this.#submenu.querySelector(':scope > .menu-drawer__submenu') ?? panel;
    trapFocus(panel);
    const back = panel.querySelector('.menu-drawer__product-back, .menu-drawer__back-button');
    if (back) back.focus({ preventScroll: true });
    else panel.focus({ preventScroll: true });
  }

  #releaseFocus() {
    if (HeaderDrawer.active === this) removeTrapFocus();
  }

  #frame(callback) {
    const id = requestAnimationFrame(() => {
      this.#frames.delete(id);
      if (this.#controller && !this.#controller.signal.aborted) callback();
    });
    this.#frames.add(id);
    return id;
  }

  #afterMotion(element, callback) {
    // Force style resolution before collecting CSS transitions/animations.
    void getComputedStyle(element).transform;
    onAnimationEnd(element, () => {
      if (this.#controller && !this.#controller.signal.aborted) callback();
    }, { subtree: false });
  }

  #suppressInitialAccordionAnimation() {
    const content = this.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => element.classList.add('details-content--no-animation'));
    // No fixed 100 ms timer: remove the suppression after the initial styles
    // have been committed. Existing accordion transition declarations are kept.
    this.#frame(() => this.#frame(() => content.forEach((element) => element.classList.remove('details-content--no-animation'))));
  }

  #pinHeader() {
    if (!this.#mobile.matches || !this.#header) return;
    if (!this.#headerSnapshot) {
      this.#headerSnapshot = { sticky: this.#header.getAttribute('data-sticky-state') };
      const rect = this.#header.getBoundingClientRect();
      const forceFixed = rect.bottom <= 0 || rect.top < -2;
      this.#headerShell?.classList.toggle('mobile-menu-header-force-fixed', forceFixed);
      this.#headerShell?.classList.add('mobile-menu-header-locked');
      this.#header.classList.add('mobile-menu-header-open');
      this.#header.setAttribute('data-sticky-state', 'active');
      document.body.classList.add('mobile-menu-drawer-open');
    }
    this.#position();
  }

  #unpinHeader() {
    if (!this.#headerSnapshot) return;
    const { sticky } = this.#headerSnapshot;
    if (sticky === null) this.#header?.removeAttribute('data-sticky-state');
    else this.#header?.setAttribute('data-sticky-state', sticky);
    this.#headerShell?.classList.remove('mobile-menu-header-locked', 'mobile-menu-header-force-fixed');
    this.#header?.classList.remove('mobile-menu-header-open');
    document.body.classList.remove('mobile-menu-drawer-open');
    this.#headerSnapshot = null;
    this.refs.details.style.removeProperty('--mobile-menu-offset');
    this.refs.details.style.removeProperty('--mobile-menu-height');
  }

  #position() {
    if (!this.#headerSnapshot || !this.#header) return;
    const rect = this.#header.getBoundingClientRect();
    // The host's server-rendered style can be replaced during header rendering.
    // Keep runtime geometry on the existing data-skip-node-update details node.
    this.refs.details.style.setProperty('--mobile-menu-offset', `${Math.ceil(Math.max(0, rect.height - HEADER_BORDER_OVERLAP))}px`);
    this.refs.details.style.setProperty('--mobile-menu-height', `calc(100dvh - ${Math.ceil(Math.max(0, rect.bottom))}px)`);
  }

  #schedulePosition = () => {
    if (!this.#headerSnapshot || this.#positionFrame !== null) return;
    this.#positionFrame = this.#frame(() => { this.#positionFrame = null; this.#position(); });
  };

  #onBreakpoint = () => {
    if (!this.isOpen) return;
    if (this.#mobile.matches) this.#pinHeader();
    else this.#unpinHeader();
    this.#focusCurrent();
  };

  #reset() {
    ++this.#generation;
    this.#operations = new WeakMap();
    for (const product of this.querySelectorAll(PRODUCT)) this.#resetProduct(product);
    for (const details of this.querySelectorAll(SUBMENU)) {
      details.classList.remove('menu-open');
      this.#setExpanded(details, false);
      const panel = details.querySelector(':scope > .menu-drawer__submenu');
      if (panel) panel.inert = false;
    }
    this.#submenu = null;
    this.#product = null;
    this.#state = 'closed';
    this.refs.details.classList.remove('menu-open');
    this.#setExpanded(this.refs.details, false);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.refs.menuDrawer.inert = true;
    this.querySelectorAll('.details-content--no-animation').forEach((element) => element.classList.remove('details-content--no-animation'));
    this.#releaseFocus();
    if (HeaderDrawer.active === this) HeaderDrawer.active = null;
    this.#unpinHeader();
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
